<template>
  <div
    class="list-title"
    :class="{ 'list-title--active': active, 'list-title--large': large }"
    @click="onTrigger"
  >
    <a
      v-if="linked"
      :href="linkHref"
      :tabindex="tabbable ? 0 : -1"
      class="list-title__label"
      :class="{
        'list-title__label--without-toggle': !hasToggle,
      }"
    >
      {{ titleLabel }}
    </a>
    <template v-else>
      <h2
        class="list-title__label"
        :tabindex="tabbable ? 0 : -1"
        @keydown="onKeydown"
      >
        {{ titleLabel }}
      </h2>
      <dotcom-icon
        v-if="hasToggle"
        :key="active"
        class="list-title__indicator"
        name="chevron-right"
        art="regular"
        color="--c-secondary-neutral-2"
        :rotation="open ? 270 : 90"
      />
      <a
        v-show="linkHref"
        class="list-title__overview-link"
        :class="{
          'list-title__overview-link--open': open,
        }"
        :href="linkHref"
      >
        {{ $t('overview') }}
      </a>
    </template>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

export default {
  name: 'ListTitle',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },
  props: {
    titleLabel: {
      type: String,
      required: true,
    },
    linkHref: {
      type: String,
      default: null,
    },
    active: Boolean,
    open: Boolean,
    hasToggle: Boolean,
    large: Boolean,
    linked: Boolean,
    tabbable: Boolean,
  },
  setup() {
    const language =
      window.Sanitas.navigationData.languages.find(item => item.active).title ||
      'de'
    const loadLabels = lang => import(`../i18n/${lang}.json`)
    provideI36n(language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  computed: {
    isMobile() {
      return !this.$mq['lg!']
    },
  },
  methods: {
    onKeydown(e) {
      if (e.keyCode === 13) {
        this.onTrigger()
      }
    },
    onTrigger() {
      if (!this.linked) {
        this.$emit('trigger')
      }
    },
  },
}
</script>

<style>
.list-title {
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  padding-right: var(--spacing-2);

  &:hover .list-title__label {
    color: var(--c-primary-color-2);
  }
}

.list-title__label {
  display: inline-flex;
  align-items: center;
  flex: 1;

  padding: var(--spacing-4) 0;
  font-size: 16px;
  font-weight: 600;

  line-height: 1.3333;
  margin-bottom: 0;
  color: var(--c-primary-neutral-1);

  &:hover,
  &:focus {
    border-bottom: none;
    color: var(--c-primary-color-2);
  }
}

.list-title--large .list-title__label {
  padding: var(--spacing-4) var(--spacing-2) var(--spacing-4) 0;
  font-weight: 300;
  font-size: 24px;
  line-height: 1.3333;
  margin-bottom: 0;
}

.list-title--active .list-title__label {
  color: var(--c-primary-color-2);
}

.list-title__label--without-toggle {
  width: 100%;
}

.list-title__overview-link {
  flex: 1 100%;
  color: var(--c-secondary-neutral-1);
  font-weight: 400;
  margin-bottom: 0;
  max-height: 0;
  opacity: 0;
  transition: max-height 300ms ease-in-out, opacity 300ms ease-in-out,
    margin-bottom 300ms ease-in-out;

  &:hover {
    border-bottom: none;
    color: var(--c-primary-color-2);
  }
}

.list-title__overview-link--open {
  visibility: visible;
  opacity: 1;
  max-height: 100px;
  margin-bottom: var(--spacing-6);
}

.list-title__indicator {
  font-size: 20px;
  width: 20px;
  margin-right: calc(var(--spacing-2) * -1);
  transition: transform 300ms ease-in-out;
}

@media (--lg) {
  .list-title__overview-link {
    display: none;
  }

  .list-title--large .list-title__label {
    font-size: 20px;
    line-height: 32px;
    letter-spacing: 0.33px;
    padding: 0;
  }
  .list-title--large.list-title--active .list-title__label {
    border-bottom: 1px solid var(--c-primary-color-2);
  }
}
</style>
